import type { AxiosError } from 'axios'
import { isAxiosError } from 'axios'

import type { APIErrorCode } from '~/api/apiTypes'
import type { TFunction } from '~/i18n'

export { isAxiosError }

export class FileDoesNotExist extends Error {
  constructor(message?: string) {
    super(message)
    this.name = 'FileDoesNotExist'
  }
}

export function errorIsObject(
  er: unknown,
): er is { message?: unknown; name?: string; isAxiosError?: boolean } {
  return typeof er === 'object' && er !== null
}

export function isNoInternetConnectionError(error: unknown) {
  return error instanceof Error && error.message === 'No internet connection'
}

function hasAPIErrorData(er: AxiosError): er is AxiosError<{
  errorCode: APIErrorCode
  error: string
  status: string
}> {
  return !!er.response && 'data' in er.response
}

export function getAPIErrorCode(er: unknown): APIErrorCode | null {
  if (isAxiosError(er) && hasAPIErrorData(er)) {
    return er.response?.data.errorCode || null
  }
  return null
}

export function improvedErrorMessage(msg: string, t?: TFunction) {
  if (msg === 'Network Error') {
    return t ? t('errors.noInternet') : msg
  }
  return msg
}

export function getErrorMessage(er: unknown, t?: TFunction): string {
  if (isAxiosError(er)) {
    if (hasAPIErrorData(er)) {
      const errorCode = er.response?.data.errorCode
      if (t && errorCode) {
        const errorKey = `errors.API._${errorCode}`
        // Dynamic key not in the typed translation union; resolve as a plain string.
        const errorMessage = (t as (key: string) => string)(errorKey)
        if (errorMessage !== errorKey) {
          return errorMessage
        }
        if (er.response?.data.error) {
          return er.response.data.error
        }
        return t('errors.API.UnknownError', { code: errorCode })
      }
      if (er.response?.data.error) {
        return er.response.data.error
      }
    }
    if (er.status === 500 || er.response?.status === 500) {
      return t ? t('errors.API.InternalError') : 'Internal Server Error'
    }
  }
  if (er instanceof Error) {
    return improvedErrorMessage(er.message, t)
  }
  if (errorIsObject(er)) {
    if (er.message && typeof er.message === 'string') {
      return improvedErrorMessage(er.message, t)
    }
  }
  if (typeof er === 'string') {
    return improvedErrorMessage(er, t)
  }
  return 'Unknown error'
}

export function isErrorWithKeys<K extends string | string[]>(
  er: unknown,
  keys: K,
): er is Record<K extends string ? K : K[number], unknown> {
  if (typeof er !== 'object' || er === null) {
    return false
  }
  if (typeof keys === 'string') {
    return keys in er
  }
  for (const key of keys) {
    if (!(key in er)) {
      return false
    }
  }
  return true
}

export enum CODE_ERRORS {
  CameraAlreadyLaunched = 'CameraAlreadyLaunched',
  SurveyNotFound = 'SurveyNotFound',
  SurveyMissingApiId = 'SurveyMissingApiId',
  SurveyInfoSyncFailed = 'SurveyInfoSyncFailed',
  EmailNotVerified = 'EmailNotVerified',
}

export class CodeError extends Error {
  code: CODE_ERRORS

  constructor(code: CODE_ERRORS, message?: string) {
    super(message)
    this.code = code
  }
}
export class HookApiError extends Error {
  errorCode: APIErrorCode | null = null

  constructor(
    public error: unknown,
    public errorMessage: string,
  ) {
    super(errorMessage)
    this.errorCode = getAPIErrorCode(error)
  }
}
